import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Download, BarChart3 } from 'lucide-react';
import Chart from 'react-apexcharts';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import useEmployeesActivity from '../../hooks/useEmployeesActivity';
import useEmployeesData from '../../hooks/useEmployeesData';
import useHeadDepartment from '../../hooks/useHeadDepartment';
import EmployeesFilterV3 from './EmployeesFilter';

const SERIES_CONFIG = {
  LINK_CLICKED: { name: 'Link Clicked', color: '#8B5CF6' },
  DONE: { name: 'Done', color: '#10B981' },
  ON_HOLD: { name: 'On Hold', color: '#F59E0B' },
  IN_PROGRESS: { name: 'In Progress', color: '#3B82F6' },
};

function ActivityChart({ startPickerValue, endPickerValue }) {
  const { departmentId } = useHeadDepartment();
  const { employeesOptions, pickedEmployees, fetchEmployees, setPickedEmployees } =
    useEmployeesData();
  const { employeesChartOptions, employeesChartSeries } = useEmployeesActivity({
    startPickerValue,
    endPickerValue,
    pickedEmployees,
  });

  const exportUrl = new URL(
    '/api/v1/users-statistic/head/export/users',
    process.env.REACT_APP_API_URL,
  );
  exportUrl.searchParams.set('startDate', startPickerValue.toISOString());
  exportUrl.searchParams.set('endDate', endPickerValue.toISOString());
  exportUrl.searchParams.set('userIds', pickedEmployees.map((e) => e.id).join(','));
  exportUrl.searchParams.set('departmentId', departmentId);

  // Map series to friendly names and colors
  const formattedSeries = useMemo(() => {
    return employeesChartSeries.map((s) => ({
      ...s,
      name: SERIES_CONFIG[s.name]?.name || s.name,
    }));
  }, [employeesChartSeries]);

  // Customize chart options for our theme
  const themedChartOptions = useMemo(() => {
    return {
      ...employeesChartOptions,
      chart: {
        ...employeesChartOptions.chart,
        toolbar: { show: false },
        fontFamily: 'inherit',
        background: 'transparent',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 400,
        },
      },
      colors: [
        SERIES_CONFIG.LINK_CLICKED.color,
        SERIES_CONFIG.DONE.color,
        SERIES_CONFIG.ON_HOLD.color,
        SERIES_CONFIG.IN_PROGRESS.color,
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      grid: {
        borderColor: 'hsl(var(--border))',
        strokeDashArray: 4,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      xaxis: {
        ...employeesChartOptions.xaxis,
        labels: {
          ...employeesChartOptions.xaxis?.labels,
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '11px',
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
        },
        axisBorder: {
          show: true,
          color: 'hsl(var(--border))',
        },
        axisTicks: {
          show: true,
          color: 'hsl(var(--border))',
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: 'hsl(var(--muted-foreground))',
            fontSize: '11px',
            fontWeight: 500,
          },
          formatter: (val) => Math.round(val),
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        floating: false,
        fontSize: '12px',
        fontWeight: 500,
        labels: {
          colors: 'hsl(var(--foreground))',
        },
        markers: {
          size: 8,
          shape: 'circle',
          offsetX: -4,
        },
        itemMargin: {
          horizontal: 12,
          vertical: 0,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => `${val} tasks`,
        },
        style: {
          fontSize: '12px',
        },
        marker: {
          show: true,
        },
      },
      fill: {
        opacity: 1,
      },
      states: {
        hover: {
          filter: {
            type: 'darken',
            value: 0.9,
          },
        },
        active: {
          filter: {
            type: 'darken',
            value: 0.85,
          },
        },
      },
    };
  }, [employeesChartOptions]);

  // Calculate totals for stats
  const totals = useMemo(() => {
    const result = { linkClicked: 0, done: 0, onHold: 0, inProgress: 0 };
    employeesChartSeries.forEach((s) => {
      const sum = s.data.reduce((acc, val) => acc + val, 0);
      if (s.name === 'LINK_CLICKED') result.linkClicked = sum;
      if (s.name === 'DONE') result.done = sum;
      if (s.name === 'ON_HOLD') result.onHold = sum;
      if (s.name === 'IN_PROGRESS') result.inProgress = sum;
    });
    return result;
  }, [employeesChartSeries]);

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Task Activity</CardTitle>
              <p className="text-sm text-muted-foreground">
                Track task progress over the selected period
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl.toString()} download>
              <Download className="h-4 w-4 mr-2" />
              Export
            </a>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <div className="text-lg font-bold text-purple-600">{totals.linkClicked}</div>
            <div className="text-xs text-muted-foreground">Links Clicked</div>
          </div>
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="text-lg font-bold text-green-600">{totals.done}</div>
            <div className="text-xs text-muted-foreground">Done</div>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="text-lg font-bold text-amber-600">{totals.onHold}</div>
            <div className="text-xs text-muted-foreground">On Hold</div>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="text-lg font-bold text-blue-600">{totals.inProgress}</div>
            <div className="text-xs text-muted-foreground">In Progress</div>
          </div>
        </div>

        {/* Employee Filter */}
        <div className="pt-2">
          <EmployeesFilterV3
            employeesOptions={employeesOptions}
            pickedEmployees={pickedEmployees}
            fetchEmployees={fetchEmployees}
            setPickedEmployees={setPickedEmployees}
          />
        </div>

        {/* Chart */}
        <div className="h-[320px] pt-2">
          <Chart options={themedChartOptions} series={formattedSeries} type="bar" height="100%" />
        </div>
      </CardContent>
    </Card>
  );
}

ActivityChart.propTypes = {
  startPickerValue: PropTypes.instanceOf(dayjs).isRequired,
  endPickerValue: PropTypes.instanceOf(dayjs).isRequired,
};

export default ActivityChart;
